import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import {
  CheckCircle, XCircle, Key, CalendarBlank, Users, ShieldCheck,
  CalendarPlus, Envelope, ArrowRight, Pencil, Prohibit,
} from '@phosphor-icons/react';
import type { AxiosError } from 'axios';
import type { BookingConfirmationDTO, BookingLookupResponse, DepositDueDTO } from '@pms/shared';
import { formatPrice } from '../lib/format';
import { escapeICSText } from '../lib/ics';
import { errorStatus, extractErrorMessage } from '../lib/error';
import {
  useBookingLookup, useResendConfirmation, useCancelBooking, useRequote, useModifyBooking,
} from '../hooks/useBooking';
import { WriteReview } from '../components/booking/WriteReview';

/** Normalized view model shared by the warm (confirm) and cold (lookup) paths. */
interface View {
  reference: string;
  status: string;
  propertyName: string;
  unitName: string;
  location: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  guests: number;
  currency: string;
  total: number;
  freeCancelUntil: string | null;
  pin?: string; // warm path only — shown once
  depositDue?: DepositDueDTO; // warm path only — informational, pay-at-property
}

const lookupToView = (r: BookingLookupResponse): View => ({
  reference: r.reference, status: r.status, propertyName: r.propertyName, unitName: r.unitName,
  location: r.location, checkIn: r.checkIn, checkOut: r.checkOut, nights: r.nights, guests: r.guests,
  currency: r.currency, total: r.total, freeCancelUntil: r.freeCancelUntil,
});

// Local calendar date (YYYY-MM-DD). Must be local, not toISOString() which is
// UTC — in the Americas that reads tomorrow after ~evening, flipping the
// manageable/reviewable/past-booking branches a day early.
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * The warm confirmation (incl. the one-time PIN) arrives only via in-memory
 * router state, which a refresh or return-visit discards — dropping the page to
 * the cold PIN-lookup form. Cache it in sessionStorage (tab-scoped, cleared when
 * the tab closes) so a reload re-hydrates the full confirmation. Session scope
 * keeps the PIN no more exposed than it already is on-screen and avoids
 * persisting it long-term; cross-device retrieval still goes through PIN lookup.
 */
const cacheKey = (ref: string) => `booking:${ref}`;

/**
 * Cached confirmation = the view model + the verified PIN. The PIN is persisted
 * separately from `view.pin` — that field is the warm path's one-time "save
 * this" reveal, which the cold lookup path deliberately omits from its view.
 * Persisting the PIN on its own means BOTH paths re-hydrate it on reload and
 * keep manage / cancel / resend / review enabled. Session-scoped only.
 */
export interface CachedBooking {
  view: View;
  pin: string;
}

export function readCachedView(ref: string): CachedBooking | null {
  if (!ref) return null;
  try {
    const raw = sessionStorage.getItem(cacheKey(ref));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedBooking> & Partial<View>;
    // New shape: { view, pin }. A legacy bare-View cache (older session/deploy)
    // is wrapped so a reload still re-hydrates instead of dropping to lookup.
    if (parsed && typeof parsed === 'object' && parsed.view) {
      return { view: parsed.view, pin: parsed.pin ?? parsed.view.pin ?? '' };
    }
    const legacy = parsed as View;
    return { view: legacy, pin: legacy.pin ?? '' };
  } catch {
    return null;
  }
}

export function writeCachedView(ref: string, view: View, pin: string): void {
  if (!ref) return;
  try {
    sessionStorage.setItem(cacheKey(ref), JSON.stringify({ view, pin }));
  } catch {
    /* private mode / quota — non-fatal; the page falls back to PIN lookup */
  }
}

/**
 * Booking confirmation + manage (/booking/:ref). Warm path renders from router state
 * (PIN once); cold load looks up by ref + PIN. Manage = cancel + change dates (ref+PIN,
 * no login). See docs/manage-booking/.
 */
export default function BookingConfirmationPage() {
  const { ref = '' } = useParams<{ ref: string }>();
  const warm = (useLocation().state as BookingConfirmationDTO | null) ?? null;

  // Warm path = fresh router state. On refresh/return that state is gone, so
  // fall back to the sessionStorage cache before dropping to the PIN-lookup form.
  const restored = useMemo<CachedBooking | null>(
    () => (warm ? { view: { ...warm }, pin: warm.pin ?? '' } : readCachedView(ref)),
    [warm, ref],
  );

  const [pin, setPin] = useState('');
  const [pinInput, setPinInput] = useState('');
  const lookup = useBookingLookup();

  const lookedUp = lookup.data;
  const initial: View | null = restored?.view ?? (lookedUp ? lookupToView(lookedUp) : null);

  useEffect(() => { if (restored?.pin) setPin(restored.pin); }, [restored]);

  function submitLookup(e: FormEvent) {
    e.preventDefault();
    if (!/^\d{4}$/.test(pinInput)) return;
    lookup.mutate({ ref, pin: pinInput }, { onSuccess: () => setPin(pinInput) });
  }

  if (!initial) {
    const status = errorStatus(lookup.error);
    const err = lookup.isError
      ? status === 404 ? 'Booking not found or the PIN is incorrect.'
        : status === 429 ? 'Too many attempts — please wait a moment.'
        : extractErrorMessage(lookup.error)
      : null;
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
          <Key className="mx-auto h-12 w-12 text-brand-blue" />
          <h1 className="mt-4 text-center text-xl font-bold text-brand-navy">View your booking</h1>
          <p className="mt-1 text-center text-sm text-gray-500">Booking <strong>{ref}</strong> — enter the 4-digit PIN from your confirmation.</p>
          <form onSubmit={submitLookup} noValidate className="mt-6">
            {err && <p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{err}</p>}
            <label htmlFor="pin" className="text-sm font-semibold text-brand-navy">PIN</label>
            <input id="pin" name="pin" inputMode="numeric" autoComplete="one-time-code" maxLength={4}
              value={pinInput} onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 tracking-[0.5em] outline-none focus-visible:ring-2 focus-visible:ring-brand-blue" />
            <button type="submit" disabled={lookup.isPending || pinInput.length !== 4}
              className="mt-4 w-full rounded-xl bg-brand-blue px-6 py-3 font-semibold text-white hover:bg-brand-blue-dark disabled:opacity-60">
              {lookup.isPending ? 'Viewing…' : 'View booking'}
            </button>
          </form>
          <Link to="/" className="mt-4 block text-center text-sm font-semibold text-brand-blue hover:underline">Back to home</Link>
        </div>
      </div>
    );
  }

  return <Details initial={initial} bookingRef={ref} pin={pin} />;
}

function Details({ initial, bookingRef, pin }: { initial: View; bookingRef: string; pin: string }) {
  const [view, setView] = useState<View>(initial);
  const [editing, setEditing] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  // Persist the current view (incl. post-cancel/modify updates) so a refresh
  // re-hydrates from cache instead of forcing a PIN re-entry. See readCachedView.
  useEffect(() => { writeCachedView(bookingRef, view, pin); }, [bookingRef, view, pin]);

  const cancelled = view.status === 'cancelled';
  const manageable = view.status === 'confirmed' && view.checkIn >= today() && !!pin;
  // Post-stay review (TASK-43): the stay is complete (checked out, or checkout
  // has passed) and we hold the PIN proof.
  const reviewable =
    !cancelled && !!pin && (view.status === 'checked_out' || view.checkOut < today());

  function addToCalendar() {
    const dt = (d: string) => d.replace(/-/g, '');
    const ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT',
      `SUMMARY:${escapeICSText(`Stay at ${view.propertyName}`)}`, `DTSTART;VALUE=DATE:${dt(view.checkIn)}`,
      `DTEND;VALUE=DATE:${dt(view.checkOut)}`, `LOCATION:${escapeICSText(view.location)}`,
      `DESCRIPTION:${escapeICSText(`Booking ${view.reference}`)}`, 'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
    const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
    const a = document.createElement('a');
    a.href = url; a.download = `booking-${view.reference}.ics`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-12">
      <div className="rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-gray-100">
        {cancelled ? (
          <>
            <XCircle className="mx-auto h-14 w-14 text-gray-400" />
            <h1 className="mt-4 text-2xl font-bold text-brand-navy">Booking cancelled</h1>
            <p className="mt-1 text-gray-600">{view.propertyName} · {view.location}</p>
            <p className="mt-2 text-sm text-gray-500">The room has been released. No charge was made.</p>
          </>
        ) : (
          <>
            <CheckCircle className="mx-auto h-14 w-14 text-brand-blue" />
            <h1 className="mt-4 text-2xl font-bold text-brand-navy">Booking confirmed</h1>
            <p className="mt-1 text-gray-600">{view.propertyName} · {view.location}</p>
          </>
        )}

        <div className="mx-auto mt-6 grid max-w-md grid-cols-2 gap-3">
          <Cell label="Reference">{view.reference}</Cell>
          {view.pin
            ? <Cell label="PIN (save this)"><span className="tracking-widest">{view.pin}</span></Cell>
            : <Cell label="PIN"><span className="text-sm font-medium text-gray-500">✓ verified</span></Cell>}
        </div>

        <dl className="mx-auto mt-6 max-w-md space-y-2 text-left text-sm">
          <Row icon={<CalendarBlank className="h-4 w-4 text-brand-blue" />}>{view.checkIn} → {view.checkOut} · {view.nights} night{view.nights === 1 ? '' : 's'}</Row>
          <Row icon={<Users className="h-4 w-4 text-brand-blue" />}>{view.guests} guest{view.guests === 1 ? '' : 's'} · {view.unitName}</Row>
          <Row icon={<ShieldCheck className="h-4 w-4 text-brand-blue" />}>
            No charge today{view.freeCancelUntil ? ` · free cancellation until ${view.freeCancelUntil.slice(0, 10)}` : ''}
          </Row>
        </dl>

        <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
          <span className="font-semibold text-brand-navy">Total</span>
          <span className="text-xl font-extrabold text-brand-navy">{formatPrice(view.total, view.currency)}</span>
        </div>

        {view.depositDue && (
          <p className="mt-3 rounded-lg bg-brand-blue-light px-4 py-2.5 text-sm font-medium text-brand-navy">
            Deposit due: <strong>{formatPrice(view.depositDue.amount, view.currency)}</strong> by {view.depositDue.dueDate}
            <span className="block text-xs font-normal text-gray-500">Payable at the property — no charge today.</span>
          </p>
        )}

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {!cancelled && <ResendButton bookingRef={bookingRef} pin={pin} />}
          <button type="button" onClick={addToCalendar} disabled={cancelled} className="inline-flex items-center gap-1.5 rounded-xl border border-primary-200 px-5 py-2.5 text-sm font-semibold text-brand-navy hover:bg-brand-blue-light disabled:opacity-50">
            <CalendarPlus className="h-4 w-4" /> Add to calendar
          </button>
          <Link to={cancelled ? '/search' : '/'} className="rounded-xl bg-brand-blue px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-blue-dark">
            {cancelled ? 'Find another stay' : 'Back to home'}
          </Link>
        </div>

        {/* Manage actions (only when confirmed + future + we hold the PIN) */}
        {manageable && !editing && (
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 border-t border-gray-100 pt-6">
            <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 rounded-xl border border-primary-200 px-5 py-2.5 text-sm font-semibold text-brand-navy hover:bg-brand-blue-light">
              <Pencil className="h-4 w-4" /> Change dates
            </button>
            <button type="button" onClick={() => setCancelOpen(true)} className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 px-5 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50">
              <Prohibit className="h-4 w-4" /> Cancel booking
            </button>
          </div>
        )}

        {manageable && editing && (
          <ChangeDatesPanel
            bookingRef={bookingRef} pin={pin} view={view}
            onClose={() => setEditing(false)}
            onModified={(r) => { setView(lookupToView(r)); setEditing(false); }}
          />
        )}
      </div>

      {reviewable && <WriteReview bookingRef={bookingRef} pin={pin} />}

      {!manageable && !reviewable && view.status !== 'cancelled' && view.checkIn < today() && (
        <p className="mt-4 text-center text-sm text-gray-500">To change a past or in-progress booking, please contact the property.</p>
      )}

      {!cancelled && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl bg-brand-blue-light px-5 py-4">
          <p className="text-sm text-brand-navy">Save this booking to your account for one-tap access next time.</p>
          <Link to="/login" className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-brand-blue hover:underline">
            Continue with email <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      {cancelOpen && (
        <CancelDialog
          bookingRef={bookingRef} pin={pin}
          onClose={() => setCancelOpen(false)}
          onCancelled={(r) => { setView(lookupToView(r)); setCancelOpen(false); setEditing(false); }}
        />
      )}
    </div>
  );
}

function ChangeDatesPanel({ bookingRef, pin, view, onClose, onModified }: {
  bookingRef: string; pin: string; view: View;
  onClose: () => void; onModified: (r: BookingLookupResponse) => void;
}) {
  const [checkIn, setCheckIn] = useState(view.checkIn);
  const [checkOut, setCheckOut] = useState(view.checkOut);
  const requote = useRequote();
  const modify = useModifyBooking();

  const datesValid = !!checkIn && !!checkOut && checkOut > checkIn;
  const quote = requote.data;
  const reqErr = requote.isError
    ? errorStatus(requote.error) === 400 ? 'Those dates aren\'t valid (future, up to 90 nights).' : extractErrorMessage(requote.error)
    : null;

  // Re-quote when the dates change (debounced-ish via blur/submit).
  function doRequote() {
    if (datesValid) requote.mutate({ ref: bookingRef, pin, checkIn, checkOut });
  }
  useEffect(() => { doRequote(); /* on mount with current dates */ /* eslint-disable-next-line */ }, []);

  function apply() {
    if (!quote || !quote.available) return;
    modify.mutate(
      { ref: bookingRef, pin, checkIn, checkOut, quotedTotal: quote.total },
      { onSuccess: onModified },
    );
  }

  const modStatus = errorStatus(modify.error);
  const modErr = modify.isError
    ? modStatus === 409
      ? (modify.error as AxiosError<{ code?: string }>)?.response?.data?.code === 'UNIT_UNAVAILABLE'
        ? 'Those dates were just taken — pick others.'
        : 'The price changed — re-check and try again.'
      : extractErrorMessage(modify.error)
    : null;

  return (
    <div className="mt-6 rounded-xl border border-gray-200 p-5 text-left">
      <h2 className="text-sm font-bold text-brand-navy">Change your dates</h2>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-gray-500">Check in
          <input type="date" value={checkIn} min={today()} onChange={(e) => setCheckIn(e.target.value)} onBlur={doRequote}
            className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-brand-blue" />
        </label>
        <label className="text-xs font-semibold text-gray-500">Check out
          <input type="date" value={checkOut} min={checkIn || today()} onChange={(e) => setCheckOut(e.target.value)} onBlur={doRequote}
            className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-brand-blue" />
        </label>
      </div>

      <div className="mt-3 min-h-[1.5rem] text-sm" aria-live="polite">
        {reqErr && <span role="alert" className="text-red-600">{reqErr}</span>}
        {modErr && <span role="alert" className="text-red-600">{modErr}</span>}
        {requote.isPending && <span className="text-gray-500">Checking…</span>}
        {quote && !requote.isPending && (quote.available
          ? <span className="text-brand-navy">New total: <strong>{formatPrice(quote.total, quote.currency)}</strong> · {quote.nights} night{quote.nights === 1 ? '' : 's'}</span>
          : <span className="text-amber-700">Not available for those dates.</span>)}
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-brand-navy">Keep current dates</button>
        <button type="button" onClick={apply} disabled={!quote?.available || modify.isPending || !datesValid}
          className="rounded-xl bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:bg-brand-blue-dark disabled:opacity-60">
          {modify.isPending ? 'Updating…' : 'Confirm change'}
        </button>
      </div>
    </div>
  );
}

function CancelDialog({ bookingRef, pin, onClose, onCancelled }: {
  bookingRef: string; pin: string; onClose: () => void; onCancelled: (r: BookingLookupResponse) => void;
}) {
  const [reason, setReason] = useState('');
  const cancel = useCancelBooking();
  const err = cancel.isError
    ? errorStatus(cancel.error) === 409 ? 'This booking can no longer be cancelled online — contact the property.'
      : errorStatus(cancel.error) === 429 ? 'Too many attempts — please wait a moment.'
      : extractErrorMessage(cancel.error)
    : null;

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div role="dialog" aria-modal="true" aria-label="Cancel booking" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-bold text-brand-navy">Cancel this booking?</h2>
        <p className="mt-2 text-sm text-gray-600">No charge was made — cancelling releases the room. This can't be undone.</p>
        <label className="mt-4 block text-xs font-semibold text-gray-500">Reason (optional)
          <input type="text" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-brand-blue" />
        </label>
        {err && <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{err}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" autoFocus onClick={onClose} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-brand-navy">Keep booking</button>
          <button type="button" disabled={cancel.isPending}
            onClick={() => cancel.mutate({ ref: bookingRef, pin, ...(reason.trim() ? { reason: reason.trim() } : {}) }, { onSuccess: onCancelled })}
            className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
            {cancel.isPending ? 'Cancelling…' : 'Confirm cancellation'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResendButton({ bookingRef, pin }: { bookingRef: string; pin: string }) {
  const resend = useResendConfirmation();
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const overLimit = errorStatus(resend.error) === 429;
  const disabled = resend.isPending || cooldown > 0 || !pin;

  return (
    <span className="inline-flex flex-col items-center">
      <button type="button" disabled={disabled}
        onClick={() => resend.mutate({ ref: bookingRef, pin }, { onSuccess: () => setCooldown(30) })}
        className="inline-flex items-center gap-1.5 rounded-xl border border-primary-200 px-5 py-2.5 text-sm font-semibold text-brand-navy hover:bg-brand-blue-light disabled:opacity-60">
        <Envelope className="h-4 w-4" />
        {resend.isPending ? 'Sending…' : resend.isSuccess && cooldown > 0 ? `Sent ✓ (${cooldown})` : 'Resend email'}
      </button>
      {overLimit && <span role="alert" className="mt-1 text-xs text-amber-700">Please wait before requesting again.</span>}
    </span>
  );
}

function Cell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-brand-blue-light p-3">
      <div className="text-xs font-semibold uppercase text-gray-500">{label}</div>
      <div className="mt-0.5 text-lg font-extrabold text-brand-navy">{children}</div>
    </div>
  );
}
function Row({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return <div className="flex items-center gap-2 text-gray-700">{icon}{children}</div>;
}
